import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { formatPrice, applyDiscount } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { PenLine, Star } from "lucide-react";
import { JerseyBackPreview } from "@/components/JerseyBackPreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductCard({ product, index = 0, rating, garmentFilter }) {
  const { discountCents, garmentColors } = useSettings();
  const [previewColor, setPreviewColor] = useState(null);
  // When a garment filter is active, reflect that garment's price + image on the card
  // (products are sold as both hoodie & shirt, so "Shirts" should show the shirt price/photo).
  const matchVariant = garmentFilter
    ? (product.variants || []).find(
        (v) => v.is_enabled && (v.options?.garment || "").toLowerCase().includes(garmentFilter)
      )
    : null;
  const matchImage = garmentFilter
    ? (product.images || []).find((u) => (u || "").toLowerCase().includes(garmentFilter))
      ?? (garmentFilter === "shirt" ? (product.images || [])[1] : (product.images || [])[0])
    : null;
  const prices = product.variants?.map((v) => v.price) || [0];
  const minPrice = Math.min(...prices);
  const basePrice = matchVariant?.price ?? product.display_price ?? minPrice;
  const cardImage = matchImage || product.images?.[0];
  const cardGarment = matchVariant?.options?.garment || ((product.display_price ?? 9999) < 4000 ? "Shirt" : "Hoodie");
  const previewImage = previewColor
    ? `${BACKEND}/api/mockup/${product.id}?color=${encodeURIComponent(previewColor)}&garment=${encodeURIComponent(cardGarment)}`
    : null;
  const salePrice = applyDiscount(basePrice, discountCents);
  const onSale = discountCents > 0 && salePrice < basePrice;
  const priceLabel = formatPrice(basePrice);
  const isCustomizable = product.tags?.includes("customizable");
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid="product-card"
        className="group block bg-card border border-white/10 hover:border-primary transition-colors duration-300"
      >
        <div className="relative overflow-hidden aspect-[4/5] bg-neutral-900">
          {product.images?.[0] ? (
            <img
              src={previewImage || cardImage}
              alt={product.title}
              className={`w-full h-full object-contain transition-all duration-500 ${isCustomizable && !previewColor ? "group-hover:opacity-0" : "group-hover:scale-105"}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20">No image</div>
          )}
          {isCustomizable && !previewColor && (
            <div
              data-testid="card-back-preview"
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            >
              <JerseyBackPreview garment={matchVariant?.options?.garment || ((product.display_price ?? 9999) < 4000 ? "Shirt" : "Hoodie")} className="h-full" />
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white/80 px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] font-bold">
                Your personalized back
              </span>
            </div>
          )}
          <span className="absolute top-2.5 left-2.5 md:top-3 md:left-3 bg-primary text-black px-2 py-0.5 md:px-2.5 md:py-1 text-[9px] md:text-[10px] uppercase tracking-[0.15em] font-bold">
            {product.category}
          </span>
          {isCustomizable && (
            <span
              data-testid="customizable-badge"
              className="absolute top-2.5 right-2.5 md:top-3 md:right-3 flex items-center gap-1 bg-black/80 backdrop-blur-sm text-primary border border-primary/40 px-1.5 py-1 md:px-2 text-[9px] md:text-[10px] uppercase tracking-[0.15em] font-bold"
            >
              <PenLine className="w-3 h-3" /> <span className="hidden sm:inline">Name & №</span>
            </span>
          )}
          {onSale && (
            <span
              data-testid="sale-badge"
              className="absolute bottom-2.5 left-2.5 md:bottom-3 md:left-3 bg-primary text-black px-2 py-0.5 md:px-2.5 md:py-1 text-[9px] md:text-[10px] uppercase tracking-[0.15em] font-bold"
            >
              {formatPrice(discountCents)} Off
            </span>
          )}
        </div>
        <div className="p-3 sm:p-4 flex items-start justify-between gap-2 border-t border-white/10">
          <div className="min-w-0">
            <h3 className="font-anton text-base sm:text-lg uppercase tracking-tight leading-tight text-white group-hover:text-primary transition-colors truncate">
              {product.title}
            </h3>
            <p className="text-[10px] sm:text-[11px] text-white/40 mt-1 uppercase tracking-[0.15em] truncate">{product.tags?.[0]}</p>
            {garmentColors?.length > 0 && (
              <span
                data-testid="card-color-dots"
                className="flex items-center gap-1 mt-1.5"
                onMouseLeave={() => setPreviewColor(null)}
              >
                {garmentColors.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    data-testid={`card-color-dot-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    title={c.name}
                    aria-label={`Preview in ${c.name}`}
                    onMouseEnter={() => setPreviewColor(c.name)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPreviewColor((prev) => (prev === c.name ? null : c.name));
                    }}
                    className={`w-3 h-3 rounded-full border transition-transform hover:scale-125 ${
                      previewColor === c.name ? "border-primary scale-125" : "border-white/25"
                    }`}
                    style={{ backgroundColor: c.swatch }}
                  />
                ))}
              </span>
            )}
            {rating && rating.count > 0 && (
              <span data-testid="card-rating" className="flex items-center gap-1 mt-1.5">
                <Star className="w-3.5 h-3.5 fill-primary text-primary" />
                <span className="text-xs font-bold text-white">{rating.average}</span>
                <span className="text-[11px] text-white/40">({rating.count})</span>
              </span>
            )}
          </div>
          {onSale ? (
            <span className="flex flex-col items-end whitespace-nowrap leading-tight">
              <span className="text-[11px] text-white/40 line-through">{priceLabel}</span>
              <span data-testid="card-sale-price" className="font-bold text-sm text-primary">{formatPrice(salePrice)}</span>
            </span>
          ) : (
            <span className="font-bold text-sm whitespace-nowrap text-white">{priceLabel}</span>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
